<script setup lang="ts">
import { BillingInfo } from '@/@types'
import { Annotation, CancelReasons, Order, OrdersnapShot, Version } from '@/@types/order'
import AdditionalInformation from '@/components/shared/admin/orders/AdditionalInformation.vue'
import Annotations from '@/components/shared/admin/orders/Annotations/Annotations.vue'
import CreateAnnotations from '@/components/shared/admin/orders/Annotations/CreateAnnotations.vue'
import CancelDetails from '@/components/shared/admin/orders/CancelDetails.vue'
import ConfirmDelete from '@/components/shared/admin/orders/ConfirmDelete.vue'
import ResponsibleForTheOrder from '@/components/shared/admin/orders/ResponsibleForTheOrder.vue'
import Tickets from '@/components/shared/admin/orders/Tickets.vue'
import UpdateStatus from '@/components/shared/admin/orders/UpdateStatus.vue'
import Details from '@/components/shared/base_orders/show/Details.vue'
import Historic from '@/components/shared/base_orders/show/Historic.vue'
import HistoricSkeleton from '@/components/shared/base_orders/show/HistoricSkeleton.vue'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Title } from '@/components/ui/title'
import { formatPrice } from '@/helpers/current-price'
import OrderLayout from '@/layouts/OrderLayout.vue'
import { Deferred, Head, Link } from '@inertiajs/vue3'
import { ArrowLeftCircle, EllipsisVertical, Import, FilePlus } from 'lucide-vue-next'

interface OrderShow extends Order {
    order_snap_shot: OrdersnapShot
}

defineProps<{
    order: OrderShow
    workshop_id: number
    cancel_reasons: CancelReasons
    billing_info: BillingInfo
    order_versions: Version[]
    mailings_versions: Version[]
    items_versions: Version[]
    annotations: Annotation[]
}>()
</script>

<template>
    <Head :title="$t('Manage orders')" />

    <OrderLayout>
        <main class="flex flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold text-white">
                <Link :href="`/admin/orders?workshop_id=${workshop_id}`"><ArrowLeftCircle class="h-8 w-8" stroke-width="1.2" /></Link>
                {{ $t('View order') }}
            </Title>

            <div class="flex flex-col gap-6 lg:flex-row">
                <div class="flex flex-1 flex-col gap-6">
                    <Card class="border-transparent">
                        <CardContent class="pt-6">
                            <div class="flex flex-col justify-between gap-8 sm:flex-row">
                                <div class="space-y-2">
                                    <h2 class="font-sans text-2xl font-semibold text-gray-800">{{ $t('Order') }}: #{{ order.id }}</h2>
                                    <p class="text-sm text-slate-500">
                                        {{ $t('Responsible for the order') }}:
                                        <strong class="text-foreground">{{ order.user.name }} - {{ order.user.email }}</strong>
                                    </p>
                                    <p class="text-sm text-slate-500" v-if="order.reason_free_order">
                                        Motivo da isenção:

                                        {{ order.reason_free_order }}
                                    </p>
                                    <p class="text-slate-500">
                                        {{ $t('Order value') }}:
                                        <span class="text-foreground text-lg font-semibold">
                                            {{ formatPrice((order.transaction_detail?.amount || order.order_snap_shot.total_cents) / 100) }}
                                        </span>
                                    </p>
                                    <p class="text-slate-500">
                                        {{ $t('Discount') }}:
                                        <span class="text-foreground text-lg font-semibold">
                                            {{ formatPrice(order.order_snap_shot.discount_cents / 100) }}
                                        </span>
                                    </p>
                                </div>

                                <div class="flex w-full max-w-64 flex-1 gap-4 self-start">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger as-child>
                                            <Button variant="outline" size="icon" class="border-slate-100"><EllipsisVertical /></Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent class="divide-y-slate-200 flex w-64 flex-col divide-y">
                                            <ResponsibleForTheOrder :order="order" :billing_info="billing_info" />
                                            <ConfirmDelete
                                                :order="order"
                                                v-if="order.status === 'canceled' || order.status === 'canceled_by_admin'"
                                            />
                                            <DropdownMenuItem v-if="order.status === 'paid' || order.status === 'released'">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    :as="Link"
                                                    :href="`/admin/orders/${order.id}/import_participants`"
                                                    class="text-muted-foreground w-full justify-start"
                                                >
                                                    <Import /> Importar participantes
                                                </Button>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem v-if="order.status === 'paid' && order.order_snap_shot.total_cents > 0 && !order.rps_id">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    @click.prevent="() => $inertia.post(`/admin/orders/${order.id}/create_rps`)"
                                                    class="text-muted-foreground w-full justify-start"
                                                >
                                                    <FilePlus /> Criar RPS
                                                </Button>
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    <div class="flex-1">
                                        <UpdateStatus :order="order" />

                                        <div class="mt-3">
                                            <div class="flex items-center gap-4" aria-label="Mudar grupo do pedido">
                                                {{ $t('Group') }}
                                            </div>

                                            <p class="text-base font-semibold">
                                                {{ $t(`groups.${order.group}`) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Tickets :order="order" />

                    <AdditionalInformation :billing_info="billing_info" />

                    <Accordion :as="Card" collapsible class="border-transparent">
                        <AccordionItem value="annotations">
                            <AccordionTrigger class="font-sans text-2xl font-semibold text-gray-800">Histórico de Anotações</AccordionTrigger>
                            <AccordionContent class="p-4 pb-6">
                                <div class="relative grid gap-y-10">
                                    <Annotations :orderId="order.id" :annotations="annotations" />

                                    <CreateAnnotations :orderId="order.id" />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    <Accordion :as="Card" collapsible class="border-transparent">
                        <AccordionItem value="order-history">
                            <AccordionTrigger class="font-sans text-2xl font-semibold text-gray-800">Históricos</AccordionTrigger>
                            <AccordionContent class="p-4 pb-6">
                                <Tabs defaultValue="order" class="w-full">
                                    <TabsList class="w-full">
                                        <TabsTrigger value="order"> Pedido </TabsTrigger>
                                        <TabsTrigger value="item"> Ingresso </TabsTrigger>
                                        <TabsTrigger value="mailing"> Participante </TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="order">
                                        <Deferred data="order_versions">
                                            <template #fallback>
                                                <HistoricSkeleton />
                                            </template>

                                            <Historic :version="order_versions" type="order" />
                                        </Deferred>
                                    </TabsContent>
                                    <TabsContent value="item">
                                        <Deferred data="order_versions">
                                            <template #fallback>
                                                <HistoricSkeleton />
                                            </template>

                                            <Historic :version="items_versions" type="item" />
                                        </Deferred>
                                    </TabsContent>
                                    <TabsContent value="mailing">
                                        <Deferred data="order_versions">
                                            <template #fallback>
                                                <HistoricSkeleton />
                                            </template>

                                            <Historic :version="mailings_versions" type="mailing" />
                                        </Deferred>
                                    </TabsContent>
                                </Tabs>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
                <div class="flex w-full max-w-96 flex-col gap-6">
                    <Details :order="order" />

                    <CancelDetails :order="order" v-if="order.cancel_reason" />
                </div>
            </div>
        </main>
    </OrderLayout>
</template>
